import type { ConversationDto, SkillDto } from '@dbrain/shared';
import { Button, Dialog, Icons, Switch } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { capKeys, useServers, useSkills } from '../../api/capabilities.js';
import type { ChatItem, SourceView } from '@dbrain/app-core/conversation-state';
import { ErrorAlert } from '../ErrorAlert.js';

/**
 * Skills, connected services and web sources inside a conversation
 * (03l2 §15, QW-1205, QW-1214, QW-BUG-050, DOC-013): what was loaded and
 * what it may run, what changed, which pages were read, and switches for
 * this conversation only.
 */
type CapabilitiesItem = Extract<ChatItem, { kind: 'capabilities' }>;

export function CapabilitiesLine({ item }: { item: CapabilitiesItem }) {
  const { t } = useTranslation();
  const name = (entry: { kind: string; name: string }) =>
    `${entry.name} (${entry.kind === 'skill' ? t('skillChip.skill') : entry.kind === 'mcp_tool' ? t('connectors.tool') : t('connectors.one')})`;
  return (
    <p className="message message--notice" data-testid="capabilities-line">
      {item.added.length > 0 ? t('skillChip.added', { list: item.added.map(name).join(', ') }) : null}
      {item.added.length > 0 && item.removed.length > 0 ? ' · ' : null}
      {item.removed.length > 0 ? t('skillChip.removed', { list: item.removed.map(name).join(', ') }) : null}
    </p>
  );
}

/** a page's host; an address a browser cannot read stays as it is (never an error while drawing) */
function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** DOC-013: every page a turn read, with the time it was read; a pill that opens the list (Adimlar.dc.html). */
export function SourcesPanel({ sources }: { sources: SourceView[] }) {
  const { t, i18n } = useTranslation();
  if (sources.length === 0) return null;
  return (
    <details className="sources-panel" data-testid="sources-panel">
      <summary>
        <Icons.integrations size={14} />
        {t('sources.title', { count: sources.length })}
      </summary>
      <ol className="sources-panel__list">
        {sources.map((source) => (
          <li key={source.url}>
            <a href={source.url} target="_blank" rel="noreferrer noopener">
              {source.title || source.url}
            </a>
            <span className="muted">
              {' '}
              · {hostnameOf(source.url)} · {new Date(source.accessedAt).toLocaleString(i18n.language)}
            </span>
          </li>
        ))}
      </ol>
    </details>
  );
}

/**
 * This conversation's switches (SA-049): skills and connected services on
 * or off here only; the wider settings stay. "Save this as a skill" starts
 * from here (SA-054).
 */
/**
 * The skills and connected services of this conversation (SA-049; the "⋯"
 * menu of docs/design/mockups/SoruKarti.dc.html): each on or off here, a
 * new skill, this conversation saved as one.
 */
export function ConversationToolsDialog({
  conversation,
  onClose,
  onSaveAsSkill,
  onAddSkill,
}: {
  conversation: ConversationDto;
  onClose: () => void;
  onSaveAsSkill: () => void;
  onAddSkill: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const place = { workspaceId: conversation.workspaceId, conversationId: conversation.id };
  const skills = useSkills(place);
  const servers = useServers(place);
  const toggleSkill = useMutation({
    mutationFn: (input: { skill: SkillDto; enabled: boolean }) => api('PUT', `/api/skills/${encodeURIComponent(input.skill.key)}/enabled`, { enabled: input.enabled, conversationId: conversation.id }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll }),
  });
  const toggleServer = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api('PUT', `/api/mcp/servers/${input.id}/enabled?workspaceId=${conversation.workspaceId}`, { enabled: input.enabled, conversationId: conversation.id }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: capKeys.serversAll }),
  });
  const shownSkills = (skills.data ?? []).filter((s) => (s.version !== null || s.level === 'builtin') && !s.shadowedBy);
  const shownServers = (servers.data ?? []).filter((s) => s.blockedReason !== 'local_in_chat');
  return (
    <Dialog
      title={t('convTools.button')}
      closeLabel={t('common.close')}
      onClose={onClose}
      dismissOnBackdrop
      data-testid="conversation-tools"
      footer={
        <>
          <Button variant="secondary" icon={<Icons.plus size={15} />} onClick={onAddSkill} data-testid="conv-skill-add">
            {t('skills.add.button')}
          </Button>
          <Button variant="secondary" onClick={onSaveAsSkill} data-testid="save-as-skill">
            {t('convTools.saveAsSkill')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('convTools.hint')}</p>
      <h3 className="tools-dialog__title">{t('convTools.skills')}</h3>
      <ul className="tools-dialog__list">
        {shownSkills.map((skill) => (
          <li key={skill.key}>
            <label className="ui-switch-row">
              <Switch
                checked={skill.enabled}
                disabled={skill.enabledScope === 'required'}
                onChange={(e) => toggleSkill.mutate({ skill, enabled: e.target.checked })}
                data-testid="conv-skill-toggle"
                data-name={skill.name}
              />
              <span>
                {skill.name} <span className="muted">· {t(`skills.levels.${skill.level}`)}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <Link to="/skills">{t('convTools.manageSkills')}</Link>
      <h3 className="tools-dialog__title">{t('convTools.connectors')}</h3>
      {shownServers.length === 0 ? <p className="muted">{t('convTools.noConnectors')}</p> : null}
      <ul className="tools-dialog__list">
        {shownServers.map((server) => (
          <li key={server.id}>
            <label className="ui-switch-row">
              <Switch
                checked={server.enabled}
                disabled={server.enabledScope === 'required'}
                onChange={(e) => toggleServer.mutate({ id: server.id, enabled: e.target.checked })}
                data-testid="conv-server-toggle"
              />
              <span>
                {server.displayName}
                {!server.usable && server.enabled ? <span className="muted"> · {t(`connectors.blocked.${server.blockedReason ?? 'error'}`)}</span> : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <Link to="/connectors">{t('convTools.manageConnectors')}</Link>
      <ErrorAlert error={toggleSkill.error ?? toggleServer.error} />
    </Dialog>
  );
}

